import { useState } from "react";
import { cn } from "@/lib/utils";
import { fallbackSrc, srcset, type Photo } from "@/lib/photos";

type SmartImageProps = {
  photo: Photo;
  sizes: string;
  className?: string;
  imgClassName?: string;
  priority?: boolean;
  onClick?: () => void;
};

export function SmartImage({
  photo,
  sizes,
  className,
  imgClassName,
  priority = false,
  onClick,
}: SmartImageProps) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className={cn("relative overflow-hidden bg-muted", className)}
      style={
        photo.position
          ? ({ backgroundPosition: photo.position } as React.CSSProperties)
          : undefined
      }
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 scale-110 bg-cover blur-xl transition-opacity duration-700"
        style={{
          backgroundImage: `url("${photo.lqip}")`,
          backgroundPosition: photo.position ?? "50% 50%",
          opacity: loaded ? 0 : 1,
        }}
      />
      <img
        src={fallbackSrc(photo)}
        srcSet={srcset(photo)}
        sizes={sizes}
        alt={photo.alt}
        width={photo.width}
        height={photo.height}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        fetchPriority={priority ? "high" : "auto"}
        onLoad={() => setLoaded(true)}
        onClick={onClick}
        className={cn(
          "relative h-full w-full object-cover transition-opacity duration-700 ease-out",
          loaded ? "opacity-100" : "opacity-0",
          onClick && "cursor-zoom-in",
          imgClassName,
        )}
      />
    </div>
  );
}
